<template>
  <v-stage
    ref="stage"
    id="plan-stage"
    :config="stageConfig"
    @wheel="handleScroll"
    @touchmove="handlePointerMove"
    @touchend="handlePointerUp"
    @click="setRoomCorner(true)"
    @mousemove="setRoomCorner(false)"
    v-show="imageWidth && imageHeight"
  >
    <v-layer>
      <v-image :config="imageConfig" ref="imageRef" />
      <room-drawing-container :scale="scale" />
    </v-layer>
  </v-stage>
</template>

<script>
import { mapActions, mapState, mapMutations } from "vuex";
import { eventBus } from "@/components/EventBus";
import RoomDrawingContainer from "./RoomDrawingContainer";
import { MAX_CANVAS_SIZE } from "@/utils/constants";

export default {
  name: "plan",
  components: {
    RoomDrawingContainer,
  },
  data: () => ({
    scale: 1,
    stageXCoordinate: 0,
    stageYCoordinate: 0,
    image: null,
    imageWidth: null,
    imageHeight: null,
    generatingPDF: false,
    lastCenter: null,
    lastDist: 0,
    roomWidth: 0,
    roomHeight: 0,
    stageHeight: window.innerHeight,
  }),
  computed: {
    ...mapState("floorStore", ["planURL", "fileName", "fileSize"]),
    ...mapState("roomStore", ["isDrawing", "corners", "longer_size_length"]),
    stageConfig() {
      return {
        width: window.innerWidth,
        height: this.stageHeight,
        draggable: true,
        scale: {
          x: this.scale,
          y: this.scale,
        },
        x: this.stageXCoordinate,
        y: this.stageYCoordinate,
      };
    },
    imageConfig() {
      return {
        image: this.image,
        width: this.imageWidth,
        height: this.imageHeight,
      };
    },
  },
  methods: {
    ...mapActions("projectStore", ["retrieveFile"]),
    ...mapMutations("loaderStore", ["setIsLoading"]),
    setRoomCorner(fixed) {
      if (this.isDrawing) {
        eventBus.$emit("addRoomCorner", fixed, this.$refs.stage);
      }
    },
    handleScroll(event) {
      event.evt ? event.evt.preventDefault() : event.preventDefault();

      const oldScale = this.scale;

      this.scale =
        event.scale ||
        (event.evt.deltaY > 0 ? this.scale * 0.95 : this.scale / 0.95);

      const absolutePosition = this.$refs.stage.getNode().getAbsolutePosition();
      const pointer = this.$refs.stage.getNode().getPointerPosition();

      const mousePointTo = {
        x: (pointer.x - absolutePosition.x) / oldScale,
        y: (pointer.y - absolutePosition.y) / oldScale,
      };

      const newPos = {
        x: pointer.x - mousePointTo.x * this.scale,
        y: pointer.y - mousePointTo.y * this.scale,
      };

      this.stageXCoordinate = newPos.x;
      this.stageYCoordinate = newPos.y;
    },

    getCenter(p1, p2) {
      return {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };
    },
    getDistance(p1, p2) {
      return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    },
    handlePointerMove(event) {
      const oldScale = this.scale;
      if (event.evt.touches.length == 2) {
        const oldDraggable = this.$refs.stage.getNode().draggable();

        if (oldDraggable) {
          this.$refs.stage.getNode().draggable(false);
        }
        const touch1 = event.evt.touches[0];
        const touch2 = event.evt.touches[1];

        const p1 = {
          x: touch1.clientX,
          y: touch1.clientY,
        };
        const p2 = {
          x: touch2.clientX,
          y: touch2.clientY,
        };
        if (!this.lastCenter) {
          this.lastCenter = this.getCenter(p1, p2);
        }
        const newCenter = this.getCenter(p1, p2);

        const absolutePosition = this.$refs.stage
          .getNode()
          .getAbsolutePosition();

        const pointTo = {
          x: (newCenter.x - absolutePosition.x) / oldScale,
          y: (newCenter.y - absolutePosition.y) / oldScale,
        };

        const dist = this.getDistance(p1, p2);
        if (!this.lastDist) {
          this.lastDist = dist;
        }
        this.scale = this.scale * (dist / this.lastDist);

        const dx = newCenter.x - this.lastCenter.x;
        const dy = newCenter.y - this.lastCenter.y;

        const newPos = {
          x: newCenter.x - pointTo.x * this.scale + dx,
          y: newCenter.y - pointTo.y * this.scale + dy,
        };

        this.stageXCoordinate = newPos.x;
        this.stageYCoordinate = newPos.y;

        this.lastDist = dist;
        this.lastCenter = newCenter;

        if (this.$refs.stage.getNode().draggable() !== oldDraggable) {
          this.$refs.stage.getNode().draggable(oldDraggable);
        }
      }
    },
    handlePointerUp() {
      this.lastCenter = null;
      this.lastDist = 0;
    },
    checkFirebaseStorageUrl(roomImageUrl) {
      const firebaseUrlString = "firebasestorage";
      return roomImageUrl.indexOf(firebaseUrlString) !== -1;
    },
    async loadImage(src) {
      if (src) {
        this.reset();
        this.setIsLoading(true);

        const s3UrlString = "amazonaws.com/";
        let fileUrl = "";

        if (this.checkFirebaseStorageUrl(src)) {
          fileUrl = src;
        } else if (src.indexOf(s3UrlString) !== -1) {
          const fileName = src.split(s3UrlString)[1];
          fileUrl = await this.retrieveFile(fileName);
        } else {
          fileUrl = await this.retrieveFile(src);
        }

        const image = new window.Image();
        image.src = fileUrl;
        image.crossOrigin = "Anonymous";
        image.onload = () => {
          this.image = image;
          let imageHeight = image.height;
          let imageWidth = image.width;
          const imageSize = imageHeight * imageWidth;
          if (imageSize > MAX_CANVAS_SIZE) {
            const coefficient = imageSize / MAX_CANVAS_SIZE;
            imageHeight /= coefficient;
            imageWidth /= coefficient;
          }
          this.imageWidth = imageWidth;
          this.imageHeight = imageHeight;
          this.scale = 1 / (imageWidth / 1000);
          this.setIsLoading(false);
        };
      }
    },
    reset() {
      this.scale = 1;
      this.stageXCoordinate = 0;
      this.stageYCoordinate = 0;
      this.image = null;
      this.imageWidth = null;
      this.imageHeight = null;
      this.generatingPDF = false;
      this.lastCenter = null;
      this.lastDist = 0;
      this.roomWidth = 0;
      this.roomHeight = 0;
      this.stageHeight = window.innerHeight;
    },
  },
  created() {
    this.loadImage(this.planURL);

    eventBus.$on("centerRoom", (savePlan) => {
      eventBus.$emit("centerRoomWithStage", this.$refs.stage, savePlan);
    });

    eventBus.$on("heightChange", (height) => {
      this.stageHeight = height;
    });
  },
  watch: {
    planURL(value) {
      this.loadImage(value);
    },
  },
  destroyed() {
    eventBus.$off("centerRoom");
    eventBus.$off("heightChange");
  },
};
</script>

<style lang="scss">
#plan-stage {
  filter: invert(0.8) brightness(1.5);
}
</style>
